// Field data-binding resolution — the ONE place the dataKey rules live on the frontend.
//
// Shared by the Live Form Preview (how it keys its typed state) and PDFUpload's generation
// overlay (how a typed value becomes the payload). Keeping both on this helper is deliberate:
// the preview must fill the exact same slots the deliverable reads, or "what you previewed" and
// "what you generated" drift — and the trim-then-default rule must not be re-implemented (and
// quietly diverge) in two places.
//
// Full contract: docs/design/FIELD_DATA_BINDING_DESIGN.md §4, §7.

/** The minimal shape these helpers read off a field. The real editor field (UIFieldCoordinates)
 *  is a superset; this keeps the util decoupled from the full type. */
export interface DataBindingField {
  id: string;
  dataKey?: string;
  defaultValue?: string;
  type?: string;
}

/**
 * The key a field's fill value lives under.
 *
 * - A non-blank `dataKey` (after trimming) is the shared binding: several fields carrying the
 *   same dataKey resolve to ONE value — type once, fills everywhere (e.g. a VIN on every page).
 * - Otherwise the field's own row `id` — a 1:1, transient key. This is NOT a persisted or shared
 *   data reference and never resolves a binding; it is only how the preview holds this one
 *   independent field's typed sample (owner decision, 18 Oct: a dataKey-less field stays fillable).
 *
 * A whitespace-only dataKey is treated as no dataKey — garbage-whitespace reduction, not content
 * judging.
 */
export function fillKeyFor(field: DataBindingField): string {
  const dk = (field.dataKey ?? '').trim();
  return dk !== '' ? dk : field.id;
}

/**
 * Resolve a field's value from the supplied fill data.
 *
 * THE SCAR (owner): the Default Value is a SEED, never an OVERRIDE. A non-empty entered value —
 * after trimming whitespace — always wins; a blank or whitespace-only entry falls back to the
 * Default Value. We test an explicit trimmed `!== ''`, never a truthy `||`, so a value that is
 * merely "falsy" is never mistaken for blank. This supersedes the 2026-08-18 overlay behavior
 * where a preview-cleared field stayed blank: under the dataKey model a blank entry takes the
 * default, consistently across the preview, the fillable PDF, the flattened PDF and the generated
 * project (which has no "untouched" state — every field submits "" when empty).
 *
 * Checkboxes carry 'true'/'false', not free text, so they are passed through without trimming;
 * a shared dataKey still shares the boolean.
 */
export function resolveFieldValue(field: DataBindingField, formData: Record<string, any>): string {
  const key = fillKeyFor(field);
  if (field.type === 'checkbox') {
    return String(formData[key] ?? field.defaultValue ?? 'false');
  }
  const entered = (formData[key] ?? '').toString().trim();
  return entered !== '' ? entered : (field.defaultValue ?? '');
}
